import React, { useEffect, useState } from 'react';
import styles from './JobMatchCategories.module.scss';
import { Card, Skeleton } from '@tcc-online/common-components';
import { Checkbox } from '@tcc-online/input-components';
import { produce } from 'immer';
import { cx } from '@emotion/css';
import BasePageLayout from '../../../../common/components/BasePageLayout/BasePageLayout';
import { useDispatch, useSelector } from 'react-redux';
import AdminJobMatchSlice from '../../../slices/AdminJobMatchSlice';
import { AppDispatch } from '../../../../reducers/store';
import useAdminProjectParams from '../../../hooks/useAdminProjectParams';
import ProjectAvailableJobMatching from '../../../types/jobMatch/ProjectAvailableJobMatching';
import ProjectQuestionnaire from '../../../types/project/ProjectQuestionnaire';
import Icon from '../../../../common/components/Icon/Icon';
import ProjectJobMatching from '../../../types/jobMatch/ProjectJobMatching';
import useAdminProjectBreadcrumbs, {
    BreadcrumbFlag,
} from '../../../hooks/useAdminProjectBreadcrumbs';
import AdminButton from '../../buttons/AdminButton/AdminButton';
import { useTranslate } from 'react-translate.ts';
import { Link } from 'react-router-dom';

const skeletonComponent = Array.from(new Array(7), (_, index: number) => (
    <Skeleton height={120} key={index} className={styles.skeleton} />
));

const breadcrumbFlags =
    BreadcrumbFlag.Products |
    BreadcrumbFlag.Projects |
    BreadcrumbFlag.ProjectDetail |
    BreadcrumbFlag.Reports |
    BreadcrumbFlag.JobMatchProfiles;

export default function JobMatchCategories() {
    const { data: available, loading: availableLoading } = useSelector(
        AdminJobMatchSlice.state.availableJobMatchings,
    );
    const {
        data: jobMatchings,
        uploading,
        loading,
    } = useSelector(AdminJobMatchSlice.state.jobMatchings);
    const dispatch = useDispatch<AppDispatch>();
    const { projectId, productId } = useAdminProjectParams();
    useEffect(() => {
        dispatch(
            AdminJobMatchSlice.actions.getJobMatchings({
                projectId,
                productId,
            }),
        );
        dispatch(
            AdminJobMatchSlice.actions.getAvailableJobMatchings({
                projectId,
                productId,
            }),
        );
    }, [dispatch, productId, projectId]);

    const [selected, setSelected] = useState<number[]>(
        getActiveJobMatchings(jobMatchings),
    );
    useEffect(() => {
        setSelected(getActiveJobMatchings(jobMatchings));
    }, [jobMatchings]);

    const breadcrumbs = useAdminProjectBreadcrumbs(breadcrumbFlags, {
        jobMatch: true,
    });
    const translate = useTranslate();

    const backLink = (
        <Link to={'../'} className={styles.link}>
            <AdminButton>{translate('Zpět')}</AdminButton>
        </Link>
    );

    return (
        <BasePageLayout breadcrumbs={breadcrumbs}>
            {backLink}
            <div className={styles.list}>
                {loading || availableLoading ? (
                    skeletonComponent
                ) : (
                    <>
                        {available.map(
                            (jobMatch: ProjectAvailableJobMatching) => (
                                <Profile
                                    key={jobMatch.id}
                                    onSelect={selectProfile({
                                        prevValues: selected,
                                        productId,
                                        projectId,
                                    })}
                                    isSelected={selected.includes(jobMatch.id)}
                                    jobMatchId={jobMatch.id}
                                    name={jobMatch.name}
                                    questionnaires={jobMatch.questionnaires}
                                    isDisabled={uploading}
                                />
                            ),
                        )}
                        {jobMatchings.map((jobMatch: ProjectJobMatching) => (
                            <Profile
                                key={jobMatch.id}
                                onSelect={selectProfile({
                                    prevValues: selected,
                                    productId,
                                    projectId,
                                })}
                                isSelected={selected.includes(
                                    jobMatch.jobMatchingId,
                                )}
                                jobMatchId={jobMatch.jobMatchingId}
                                name={jobMatch.jobMatchingName}
                                questionnaires={jobMatch.questionnaires}
                                isDisabled={uploading}
                            />
                        ))}
                    </>
                )}
            </div>
            {backLink}
        </BasePageLayout>
    );

    function selectProfile(params: {
        prevValues: number[];
        productId: number;
        projectId: number;
    }) {
        return (id: number) => {
            const { prevValues, projectId, productId } = params;
            const newSelected = produce(prevValues, (draft) => {
                const index = draft.findIndex((selected) => selected === id);
                if (index >= 0) {
                    delete draft[index];
                } else {
                    draft.push(id);
                }
            });

            setSelected(newSelected);

            dispatch(
                AdminJobMatchSlice.actions.assignProject({
                    productId,
                    projectId,
                    jobMatchings: newSelected.map((jobMatchingId: number) => ({
                        jobMatchingId,
                    })),
                }),
            );
        };
    }

    function getActiveJobMatchings(jobMatchings: ProjectJobMatching[]) {
        return jobMatchings.map((jobMatching) => jobMatching.jobMatchingId);
    }
}

type ProfileProps = {
    onSelect: (id: number) => void;
    isSelected: boolean;
    isDisabled?: boolean;
    jobMatchId: number;
    name: string;
    questionnaires: ProjectQuestionnaire[];
};

function Profile(props: ProfileProps) {
    const {
        onSelect,
        jobMatchId,
        name,
        questionnaires,
        isSelected,
        isDisabled,
    } = props;

    return (
        <Card
            className={cx(
                styles.profile,
                isSelected && styles.selected,
                isDisabled && styles.disabled,
            )}
            onClick={handleSelect(jobMatchId, !!isDisabled)}
        >
            <div className={styles.check}>
                <Checkbox checked={isSelected} disabled={isDisabled} />
            </div>
            <div className={styles.name}>{name}</div>
            {/* <div className={styles.desc}>{profile.desc}</div>*/}
            <div className={styles.questionnaires}>
                {questionnaires.map((questionnaire: ProjectQuestionnaire) => (
                    <Icon name={`${questionnaire.internalName}.svg`} />
                ))}
            </div>
        </Card>
    );

    function handleSelect(id: number, disabled: boolean) {
        return () => {
            if (disabled) {
                return;
            }

            onSelect(id);
        };
    }
}
